import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserCircle, RefreshCw, Sparkles, CheckCircle2, XCircle,
  AlertCircle, ChevronDown, ChevronUp, Shield, Zap,
  Activity as ActivityIcon, Inbox as InboxIcon, BarChart3, Settings as SettingsIcon,
} from 'lucide-react';
import { T, springs } from '../lib/tokens';
import { fmtRelative } from '../lib/helpers';
import { apiFetch, isLocalAvailable } from '../lib/api';
import { Glass } from '../components/glass/Glass';
import { Md } from '../components/primitives/Md';
import { Btn } from '../components/primitives/Btn';
import { Pill } from '../components/primitives/Badge';

// AssistantView renders one persona's tab (Kay or Saul). Driven entirely by the
// `persona` slug prop — display metadata comes from the fetched ea_personas row.

const FALLBACK_NAME = { kay: 'Kay', saul: 'Saul', penny: 'Penny' };

// Fetch + parse JSON, returning null on any failure — a not-ok status, a
// network error, or an HTML fallback page (e.g. while the backend is mid-deploy).
// Lets one missing endpoint degrade to an empty section instead of erroring the tab.
async function safeJson(path) {
  try {
    const res = await apiFetch(path);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

const SUB_TABS = [
  { id: 'today',    label: 'Today',    icon: UserCircle    },
  { id: 'inbox',    label: 'Inbox',    icon: InboxIcon     },
  { id: 'activity', label: 'Activity', icon: ActivityIcon  },
  { id: 'skills',   label: 'Skills',   icon: Zap           },
  { id: 'trust',    label: 'Trust',    icon: BarChart3     },
  { id: 'rules',    label: 'Rules',    icon: Shield        },
  { id: 'settings', label: 'Settings', icon: SettingsIcon  },
];

const KIND_LABEL = {
  nightly_brief:    'Nightly brief',
  morning_brief:    'Morning brief',
  conflict_flag:    'Conflict',
  move_task:        'Task move',
  move_event:       'Event move',
  draft_reply:      'Draft reply',
  logistics_intake: 'Logistics',
};

const STATUS_COLOR = {
  pending:       { bg: 'rgba(212,146,30,0.10)', fg: T.amber },
  accepted:      { bg: 'rgba(52,168,83,0.10)',  fg: T.green || '#34a853' },
  rejected:      { bg: 'rgba(224,88,88,0.10)',  fg: T.red },
  auto_executed: { bg: 'rgba(139,114,200,0.10)',fg: T.violet },
  expired:       { bg: 'rgba(0,0,0,0.05)',      fg: T.textMuted },
  superseded:    { bg: 'rgba(0,0,0,0.05)',      fg: T.textMuted },
};

const StatusChip = ({ status }) => {
  const c = STATUS_COLOR[status] || STATUS_COLOR.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: 12,
      fontSize: 10, fontWeight: 600, letterSpacing: '0.04em',
      background: c.bg, color: c.fg, textTransform: 'uppercase',
    }}>{status}</span>
  );
};

const ProposalCard = ({ proposal, onAccept, onReject, defaultOpen = false, kindLabels }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const isPending = proposal.status === 'pending';

  const handle = async (action) => {
    setBusy(true);
    try {
      if (action === 'accept') await onAccept(proposal.id, note);
      else await onReject(proposal.id, note);
    } finally {
      setBusy(false);
      setNote('');
    }
  };

  return (
    <Glass depth={1} hover={false} style={{ padding: 0, marginBottom: 12, overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{
              fontSize: 10, fontWeight: 600, color: T.textMuted,
              padding: '2px 7px', borderRadius: 8, background: 'rgba(0,0,0,0.04)',
              letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>{kindLabels?.[proposal.kind] || KIND_LABEL[proposal.kind] || proposal.kind}</span>
            <StatusChip status={proposal.status} />
            <span style={{ fontSize: 11, color: T.textLight }}>
              {fmtRelative(proposal.created_at)}
            </span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{proposal.title}</div>
          {proposal.summary && (
            <div style={{ fontSize: 12, color: T.textMid, marginTop: 4 }}>{proposal.summary}</div>
          )}
        </div>
        <button onClick={() => setOpen(o => !o)} style={{
          background: T.amberLight, border: 'none', borderRadius: 8, cursor: 'pointer',
          padding: '6px 12px', color: T.amber, fontSize: 12, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'inherit',
        }}>
          {open ? <><ChevronUp size={12} /> Hide</> : <><ChevronDown size={12} /> View</>}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={springs.gentle}
            style={{ overflow: 'hidden' }}
          >
            <div style={{
              padding: '18px 22px 22px',
              borderTop: '1px solid rgba(0,0,0,0.05)',
              background: 'rgba(255,255,255,0.4)',
            }}>
              {proposal.body_md ? <Md>{proposal.body_md}</Md> : (
                <div style={{ fontSize: 12, color: T.textMuted, fontStyle: 'italic' }}>No body.</div>
              )}

              {proposal.payload?.executed?.summary && (
                <div style={{ marginTop: 12, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
                  color: T.green || '#34a853' }}>
                  <CheckCircle2 size={13} /> {proposal.payload.executed.summary}
                </div>
              )}
              {proposal.payload?.execution_error && (
                <div style={{ marginTop: 12, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, color: T.red }}>
                  <AlertCircle size={13} /> Action failed: {proposal.payload.execution_error}
                </div>
              )}

              {isPending && (
                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                  <input
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="Optional note (why? this trains the learning loop)"
                    style={{
                      width: '100%', padding: '8px 10px',
                      borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
                      background: 'rgba(255,255,255,0.7)', fontSize: 12,
                      fontFamily: 'inherit', color: T.text, outline: 'none',
                      marginBottom: 10,
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn variant="primary" onClick={() => handle('accept')} disabled={busy}>
                      <CheckCircle2 size={14} /> Accept
                    </Btn>
                    <Btn variant="danger" onClick={() => handle('reject')} disabled={busy}>
                      <XCircle size={14} /> Reject
                    </Btn>
                  </div>
                </div>
              )}

              {!isPending && proposal.feedback_note && (
                <div style={{ marginTop: 14, fontSize: 11, color: T.textMuted, fontStyle: 'italic' }}>
                  Note: {proposal.feedback_note}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Glass>
  );
};

// ─── Sub-panels ──────────────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 600, color: T.textLight,
    letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
    {children}
  </div>
);

const DAY_LABEL = { mon: 'Mondays', tue: 'Tuesdays', wed: 'Wednesdays', thu: 'Thursdays', fri: 'Fridays', sat: 'Saturdays', sun: 'Sundays' };
const ordinal = (n) => `${n}${[11, 12, 13].includes(n % 100) ? 'th' : ['th', 'st', 'nd', 'rd'][Math.min(n % 10, 4)] || 'th'}`;

const TodayPanel = ({ today, name, persona, onAccept, onReject, onRunBrief, running, onRunCheckin, runningCheckin, kindLabels }) => {
  const latestBrief = today?.latestBrief;
  const otherPending = (today?.pendingProposals || []).filter(p => p.id !== latestBrief?.id);
  const p = today?.persona;
  // Penny's world is money, not the calendar — her brief is weekly, her
  // check-ins open conversations in chat, and scheduler conflicts aren't hers.
  const isFinancePersona = persona === 'penny';
  const briefLabel = isFinancePersona ? 'Money brief' : (p?.brief_kind === 'morning' ? 'Morning brief' : 'Nightly brief');
  const cadence = (p?.brief_days?.length)
    ? `${briefLabel} fires ${p.brief_days.map(d => DAY_LABEL[d] || d).join(', ')} at ${p?.brief_time || '—'}`
    : `${briefLabel} fires daily at ${p?.brief_time || '—'}`;
  const hasCheckins = (p?.checkin_dates?.length || 0) > 0;
  const checkinNote = hasCheckins
    ? ` Payday check-ins open in your chat on the ${p.checkin_dates.map(ordinal).join(' and the ')} of each month.`
    : '';
  const weeklyAskNote = (p?.weekly_ask_days?.length)
    ? ` Balance refresh ${p.weekly_ask_days.map(d => DAY_LABEL[d] || d).join(', ')}.`
    : '';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 13, color: T.textMid }}>
            {p?.enabled === false
              ? <><AlertCircle size={12} style={{ verticalAlign: -2 }} /> {name} is paused — switch on in Settings.</>
              : <>{name} is on. {cadence} {p?.timezone || 'Europe/London'}.{checkinNote}{weeklyAskNote}</>}
          </div>
        </div>
        {hasCheckins && (
          <Btn variant="glass" small onClick={onRunCheckin} disabled={runningCheckin || !isLocalAvailable()}>
            <UserCircle size={12} className={runningCheckin ? 'spin' : ''} /> {runningCheckin ? 'Opening…' : 'Run check-in now'}
          </Btn>
        )}
        <Btn variant="glass" small onClick={onRunBrief} disabled={running || !isLocalAvailable()}>
          <Sparkles size={12} className={running ? 'spin' : ''} /> {running ? 'Generating…' : 'Generate brief now'}
        </Btn>
      </div>

      {latestBrief ? (
        <div style={{ marginBottom: 22 }}>
          <SectionLabel>Latest brief</SectionLabel>
          <ProposalCard proposal={latestBrief} onAccept={onAccept} onReject={onReject} defaultOpen kindLabels={kindLabels} />
        </div>
      ) : (
        <Glass depth={0} style={{ padding: 28, textAlign: 'center', marginBottom: 22 }} hover={false}>
          <UserCircle size={28} color={T.textLight} style={{ margin: '0 auto 8px', display: 'block' }} />
          <div style={{ fontSize: 13, color: T.textMuted }}>
            {isFinancePersona
              ? <>No money briefs yet — the first lands {p?.brief_days?.length ? p.brief_days.map(d => DAY_LABEL[d] || d).join(', ').replace(/s$/, '') : 'soon'} at {p?.brief_time || '07:30'}. To get value today, say hello in the chat and run the intake — or generate a brief now.</>
              : <>No briefs yet — {name} writes the first at {p?.brief_time || 'the scheduled time'}, or generate one now.</>}
          </div>
        </Glass>
      )}

      {otherPending.length > 0 && (
        <div>
          <SectionLabel>Other pending ({otherPending.length})</SectionLabel>
          {otherPending.map(prop => (
            <ProposalCard key={prop.id} proposal={prop} onAccept={onAccept} onReject={onReject} kindLabels={kindLabels} />
          ))}
        </div>
      )}

      {!isFinancePersona && (today?.activeConflicts?.length || 0) > 0 && (
        <div style={{ marginTop: 22 }}>
          <SectionLabel>Active conflicts in your calendar</SectionLabel>
          <Glass depth={0} style={{ padding: 14 }} hover={false}>
            <div style={{ fontSize: 12, color: T.textMid }}>
              {today.activeConflicts.length} conflict{today.activeConflicts.length === 1 ? '' : 's'} flagged by the scheduler.
              {' '}{name} will reference them in the next brief.
            </div>
          </Glass>
        </div>
      )}
    </div>
  );
};

const ProposalList = ({ proposals, onAccept, onReject, empty, kindLabels }) => {
  if (!proposals.length) {
    return (
      <Glass depth={0} style={{ padding: 28, textAlign: 'center' }} hover={false}>
        <div style={{ fontSize: 13, color: T.textMuted }}>{empty}</div>
      </Glass>
    );
  }
  return proposals.map(p => (
    <ProposalCard key={p.id} proposal={p} onAccept={onAccept} onReject={onReject} kindLabels={kindLabels} />
  ));
};

const TrustPanel = ({ signatures }) => {
  if (!signatures.length) {
    return (
      <Glass depth={0} style={{ padding: 28, textAlign: 'center' }} hover={false}>
        <div style={{ fontSize: 13, color: T.textMuted }}>
          No patterns observed yet. Once proposals are accepted or rejected, signatures appear here.
        </div>
      </Glass>
    );
  }
  return (
    <Glass depth={1} style={{ padding: 0, overflow: 'hidden' }} hover={false}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Learning patterns</div>
        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>
          When a pattern reaches 10 consecutive accepts it becomes eligible for auto-promotion.
          Phase 1: read-only — no auto-promotion yet.
        </div>
      </div>
      {signatures.map(s => {
        const total = s.total_proposed || 0;
        const acceptRate = total > 0 ? Math.round(((s.accepted || 0) / total) * 100) : 0;
        return (
          <div key={s.signature} style={{
            padding: '12px 18px',
            borderBottom: '1px solid rgba(0,0,0,0.04)',
            display: 'grid', gridTemplateColumns: '1fr auto auto auto auto', gap: 14, alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{s.signature}</div>
              {s.description && (
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{s.description}</div>
              )}
            </div>
            <div style={{ fontSize: 11, color: T.textMuted, textAlign: 'right' }}>
              <div style={{ fontWeight: 600, color: T.text }}>{total}</div>
              <div>proposed</div>
            </div>
            <div style={{ fontSize: 11, color: T.textMuted, textAlign: 'right' }}>
              <div style={{ fontWeight: 600, color: T.green || '#34a853' }}>{s.accepted || 0}</div>
              <div>accepted</div>
            </div>
            <div style={{ fontSize: 11, color: T.textMuted, textAlign: 'right' }}>
              <div style={{ fontWeight: 600, color: T.red }}>{s.rejected || 0}</div>
              <div>rejected</div>
            </div>
            <div style={{ fontSize: 11, textAlign: 'right' }}>
              <div style={{ fontWeight: 600, color: T.text }}>{acceptRate}%</div>
              <div style={{ color: T.textMuted }}>accept rate</div>
            </div>
          </div>
        );
      })}
    </Glass>
  );
};

const RulesPanel = ({ rules, onToggle }) => {
  if (!rules.length) {
    return <Glass depth={0} style={{ padding: 28, textAlign: 'center' }} hover={false}>
      <div style={{ fontSize: 13, color: T.textMuted }}>No rules configured for this persona.</div>
    </Glass>;
  }
  return rules.map(r => (
    <Glass key={r.rule_key} depth={1} hover={false} style={{ padding: '14px 18px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: 'monospace' }}>
            {r.rule_key}
          </div>
          <div style={{ fontSize: 12, color: T.textMid, marginTop: 4 }}>{r.description}</div>
          {r.config && Object.keys(r.config).length > 0 && (
            <div style={{
              fontSize: 11, color: T.textLight, marginTop: 6,
              fontFamily: 'monospace',
              background: 'rgba(0,0,0,0.03)',
              padding: '4px 8px', borderRadius: 6,
              whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            }}>{JSON.stringify(r.config)}</div>
          )}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: T.textMid }}>
          <input
            type="checkbox"
            checked={!!r.enabled}
            onChange={e => onToggle(r.rule_key, e.target.checked)}
            style={{ accentColor: T.amber }}
          />
          {r.enabled ? 'Enabled' : 'Disabled'}
        </label>
      </div>
    </Glass>
  ));
};

const VALID_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const SettingsPanel = ({ settings, onSave }) => {
  const [form, setForm] = useState(settings || {});
  const [briefDaysText, setBriefDaysText] = useState((settings?.brief_days || []).join(', '));
  const [checkinDatesText, setCheckinDatesText] = useState((settings?.checkin_dates || []).join(', '));
  const [weeklyAskText, setWeeklyAskText] = useState((settings?.weekly_ask_days || []).join(', '));
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(settings || {});
    setBriefDaysText((settings?.brief_days || []).join(', '));
    setCheckinDatesText((settings?.checkin_dates || []).join(', '));
    setWeeklyAskText((settings?.weekly_ask_days || []).join(', '));
  }, [settings]);

  if (!settings) {
    return <Glass depth={0} style={{ padding: 28, textAlign: 'center' }} hover={false}>
      <div style={{ fontSize: 13, color: T.textMuted }}>Settings unavailable.</div>
    </Glass>;
  }

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const parseDays = (s) => [...new Set(s.split(/[,\s]+/)
        .map(d => d.trim().toLowerCase().slice(0, 3)).filter(d => VALID_DAYS.includes(d)))];
      const days = parseDays(briefDaysText);
      const weekly = parseDays(weeklyAskText);
      const dates = [...new Set(checkinDatesText.split(/[,\s]+/)
        .map(Number).filter(n => Number.isInteger(n) && n >= 1 && n <= 31))].sort((a, b) => a - b);
      await onSave({
        ...form,
        brief_days: days.length ? days : null,
        checkin_dates: dates.length ? dates : null,
        weekly_ask_days: weekly.length ? weekly : null,
      });
    } finally { setSaving(false); }
  };

  const scheduleInputStyle = {
    width: '100%', padding: '9px 12px',
    borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
    background: 'rgba(255,255,255,0.7)', fontSize: 13,
    fontFamily: 'inherit', color: T.text, outline: 'none',
  };

  const Field = ({ label, k, type = 'text' }) => (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: T.textMid, marginBottom: 5,
        letterSpacing: '0.04em', textTransform: 'uppercase' }}>{label}</div>
      {type === 'textarea' ? (
        <textarea
          value={form[k] || ''} onChange={e => update(k, e.target.value)}
          rows={3}
          style={{
            width: '100%', padding: '9px 12px',
            borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
            background: 'rgba(255,255,255,0.7)', fontSize: 13,
            fontFamily: 'inherit', color: T.text, outline: 'none', resize: 'vertical',
          }}
        />
      ) : (
        <input
          type={type}
          value={form[k] || ''} onChange={e => update(k, e.target.value)}
          style={{
            width: '100%', padding: '9px 12px',
            borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
            background: 'rgba(255,255,255,0.7)', fontSize: 13,
            fontFamily: 'inherit', color: T.text, outline: 'none',
          }}
        />
      )}
    </label>
  );

  return (
    <Glass depth={1} hover={false} style={{ padding: 22, maxWidth: 560 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={!!form.enabled}
          onChange={e => update('enabled', e.target.checked)}
          style={{ accentColor: T.amber, width: 16, height: 16 }}
        />
        <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{form.name || 'This assistant'} is active</span>
        <span style={{ fontSize: 11, color: T.textMuted, marginLeft: 'auto' }}>
          {form.enabled ? 'Generating briefs and watching for conflicts' : 'Paused — nothing fires'}
        </span>
      </label>

      <Field label="Name" k="name" />
      <Field label="Role" k="role" />
      <Field label="Tagline" k="tagline" />
      <Field label="Signature block" k="signature_block" type="textarea" />
      <Field label="Brief time (HH:MM)" k="brief_time" />
      <label style={{ display: 'block', marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: T.textMid, marginBottom: 5,
          letterSpacing: '0.04em', textTransform: 'uppercase' }}>Brief days (e.g. mon, fri — blank = every day)</div>
        <input value={briefDaysText} onChange={e => setBriefDaysText(e.target.value)} style={scheduleInputStyle} />
      </label>
      <label style={{ display: 'block', marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: T.textMid, marginBottom: 5,
          letterSpacing: '0.04em', textTransform: 'uppercase' }}>Check-in days of month (e.g. 1, 15 — blank = none)</div>
        <input value={checkinDatesText} onChange={e => setCheckinDatesText(e.target.value)} style={scheduleInputStyle} />
      </label>
      <label style={{ display: 'block', marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: T.textMid, marginBottom: 5,
          letterSpacing: '0.04em', textTransform: 'uppercase' }}>Weekly balance-ask day (e.g. fri — blank = none)</div>
        <input value={weeklyAskText} onChange={e => setWeeklyAskText(e.target.value)} style={scheduleInputStyle} />
      </label>
      <Field label="Timezone" k="timezone" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Working hours start" k="working_hours_start" />
        <Field label="Working hours end" k="working_hours_end" />
      </div>

      <Btn variant="primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save settings'}
      </Btn>
    </Glass>
  );
};

const SkillsPanel = ({ skills, personaName, onToggle }) => {
  if (!skills.length) {
    return <Glass depth={0} style={{ padding: 28, textAlign: 'center' }} hover={false}>
      <div style={{ fontSize: 13, color: T.textMuted }}>No skills assigned to {personaName} yet.</div>
    </Glass>;
  }
  return (
    <div>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 14 }}>
        What {personaName} can draw on. Skills become invokable in chat (Phase D) — for now this is the roster.
      </div>
      {skills.map(s => (
        <Glass key={s.slug} depth={1} hover={false} style={{ padding: '14px 18px', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{s.name}</span>
                {s.category && (
                  <span style={{ fontSize: 9, fontWeight: 600, color: T.textMuted, background: 'rgba(0,0,0,0.05)',
                    padding: '2px 7px', borderRadius: 6, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{s.category}</span>
                )}
                {s.personas?.length > 1 && (
                  <span style={{ fontSize: 9, fontWeight: 600, color: T.violet, background: 'rgba(139,114,200,0.12)',
                    padding: '2px 7px', borderRadius: 6, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Shared</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: T.textMid, marginTop: 4 }}>{s.description}</div>
              {s.source_agent && (
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>via the {s.source_agent} agent</div>
              )}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: T.textMid }}>
              <input type="checkbox" checked={!!s.enabled} onChange={e => onToggle(s.slug, e.target.checked)}
                style={{ accentColor: T.amber }} />
              {s.enabled ? 'Enabled' : 'Disabled'}
            </label>
          </div>
        </Glass>
      ))}
    </div>
  );
};

// ─── Main view ───────────────────────────────────────────────────────────────

export const AssistantView = ({ persona }) => {
  const [tab, setTab] = useState('today');
  const [today, setToday] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [activity, setActivity] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [rules, setRules] = useState([]);
  const [skills, setSkills] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [runningCheckin, setRunningCheckin] = useState(false);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    if (!isLocalAvailable()) {
      setError('Local server required');
      return;
    }
    setLoading(true);
    setError(null);
    const pq = `persona=${persona}`;
    try {
      const [todayR, proposalsR, activityR, sigsR, rulesR, settingsR, skillsR, rejR] = await Promise.all([
        safeJson(`/ea/today?${pq}`),
        safeJson(`/ea/proposals?status=pending&${pq}`),
        safeJson(`/ea/proposals?status=accepted&limit=20&${pq}`),
        safeJson(`/ea/signatures?${pq}`),
        safeJson(`/ea/rules?${pq}`),
        safeJson(`/ea/settings?${pq}`),
        safeJson(`/ea/skills?${pq}`),
        safeJson(`/ea/proposals?status=rejected&limit=20&${pq}`),
      ]);
      setToday(todayR);
      setProposals(proposalsR?.proposals || []);
      const merged = [...(activityR?.proposals || []), ...(rejR?.proposals || [])]
        .sort((a, b) => new Date(b.resolved_at || b.created_at) - new Date(a.resolved_at || a.created_at))
        .slice(0, 30);
      setActivity(merged);
      setSignatures(sigsR?.signatures || []);
      setRules(rulesR?.rules || []);
      setSkills(skillsR?.skills || []);
      setSettings(settingsR?.settings || null);
      // A missing core response means the backend is unreachable (e.g. mid-deploy).
      // Partial gaps just leave a section empty rather than erroring the whole tab.
      setError(todayR ? null : "Couldn't reach the assistant backend — it may be mid-deploy. Hit Refresh shortly.");
    } finally {
      setLoading(false);
    }
  }, [persona]);

  useEffect(() => { setTab('today'); load(); }, [load]);

  const handleAccept = useCallback(async (id, note) => {
    const res = await apiFetch(`/ea/proposals/${id}/accept`, { method: 'POST', body: JSON.stringify({ note }) });
    let data = {};
    try { data = await res.json(); } catch {}
    if (data.executed?.summary) setFlash({ kind: 'ok', text: data.executed.summary });
    else if (data.execution_error) setFlash({ kind: 'err', text: `Couldn't complete the action: ${data.execution_error}` });
    else setFlash(null);
    await load();
  }, [load]);

  const handleReject = useCallback(async (id, note) => {
    await apiFetch(`/ea/proposals/${id}/reject`, { method: 'POST', body: JSON.stringify({ note }) });
    await load();
  }, [load]);

  const runBrief = useCallback(async () => {
    setRunning(true);
    try {
      const res = await apiFetch('/ea/brief/run', { method: 'POST', body: JSON.stringify({ persona }) });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `Status ${res.status}`);
      }
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  }, [load, persona]);

  const runCheckin = useCallback(async () => {
    setRunningCheckin(true);
    try {
      const res = await apiFetch('/ea/checkin/run', { method: 'POST', body: JSON.stringify({ persona }) });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `Status ${res.status}`);
      }
      setFlash({ kind: 'ok', text: 'Check-in posted — open the chat (bottom-right) to reply.' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRunningCheckin(false);
    }
  }, [load, persona]);

  const saveSettings = useCallback(async (next) => {
    const res = await apiFetch(`/ea/settings?persona=${persona}`, { method: 'PUT', body: JSON.stringify(next) });
    if (!res.ok) throw new Error('Save failed');
    await load();
  }, [load, persona]);

  const toggleRule = useCallback(async (rule_key, enabled) => {
    await apiFetch(`/ea/rules/${encodeURIComponent(rule_key)}`, {
      method: 'PUT', body: JSON.stringify({ enabled }),
    });
    await load();
  }, [load]);

  const toggleSkill = useCallback(async (slug, enabled) => {
    await apiFetch(`/ea/skills/${encodeURIComponent(slug)}`, {
      method: 'PUT', body: JSON.stringify({ enabled }),
    });
    await load();
  }, [load]);

  const counts = useMemo(() => ({
    inbox: proposals.length,
    activity: activity.length,
  }), [proposals, activity]);

  const pMeta = today?.persona;
  const name = pMeta?.name || FALLBACK_NAME[persona] || 'Assistant';
  const accent = pMeta?.accent_color || T.amber;
  const kindLabels = persona === 'penny' ? { morning_brief: 'Money brief' } : undefined;
  const subtitle = pMeta
    ? `${pMeta.role}${pMeta.tagline ? ` — ${pMeta.tagline}` : ''} · observer mode`
    : 'observer mode';

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 13, flexShrink: 0,
            background: `linear-gradient(135deg, ${accent}E6, ${accent})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 19, fontWeight: 700,
            boxShadow: `0 2px 10px ${accent}44`,
          }}>{name[0]}</div>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: T.text, letterSpacing: '-0.03em' }}>
              {name}
            </h1>
            <p style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>{subtitle}</p>
          </div>
        </div>
        <Btn variant="ghost" small onClick={load} disabled={loading}>
          <RefreshCw size={12} className={loading ? 'spin' : ''} /> Refresh
        </Btn>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {SUB_TABS.map(t => {
          const count = t.id === 'inbox' ? counts.inbox : t.id === 'activity' ? counts.activity : null;
          return (
            <Pill key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <t.icon size={12} />
                {t.label}
                {count !== null && count > 0 && (
                  <span style={{
                    background: tab === t.id ? T.amber : 'rgba(0,0,0,0.08)',
                    color: tab === t.id ? '#fff' : T.textMid,
                    borderRadius: 10, fontSize: 9, fontWeight: 700,
                    padding: '1px 6px', minWidth: 16, textAlign: 'center', marginLeft: 2,
                  }}>{count}</span>
                )}
              </span>
            </Pill>
          );
        })}
      </div>

      {error && (
        <Glass depth={0} style={{ padding: 14, marginBottom: 14 }} hover={false}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.red, fontSize: 13 }}>
            <AlertCircle size={14} /> {error}
          </div>
        </Glass>
      )}

      {flash && (
        <Glass depth={0} style={{ padding: 14, marginBottom: 14 }} hover={false}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
            color: flash.kind === 'ok' ? (T.green || '#34a853') : T.red }}>
            {flash.kind === 'ok' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />} {flash.text}
            <button onClick={() => setFlash(null)} style={{
              marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
              color: T.textMuted, fontSize: 16, lineHeight: 1, padding: 0, fontFamily: 'inherit',
            }}>×</button>
          </div>
        </Glass>
      )}

      {tab === 'today' && (
        <TodayPanel
          today={today} name={name} persona={persona}
          onAccept={handleAccept} onReject={handleReject}
          onRunBrief={runBrief} running={running}
          onRunCheckin={runCheckin} runningCheckin={runningCheckin}
          kindLabels={kindLabels}
        />
      )}

      {tab === 'inbox' && (
        <ProposalList
          proposals={proposals}
          onAccept={handleAccept} onReject={handleReject}
          empty={`No pending proposals. ${name} will surface things here as they come up.`}
          kindLabels={kindLabels}
        />
      )}

      {tab === 'activity' && (
        <ProposalList
          proposals={activity}
          onAccept={handleAccept} onReject={handleReject}
          empty="No activity yet."
          kindLabels={kindLabels}
        />
      )}

      {tab === 'skills' && <SkillsPanel skills={skills} personaName={name} onToggle={toggleSkill} />}

      {tab === 'trust' && <TrustPanel signatures={signatures} />}

      {tab === 'rules' && <RulesPanel rules={rules} onToggle={toggleRule} />}

      {tab === 'settings' && <SettingsPanel settings={settings} onSave={saveSettings} />}
    </motion.div>
  );
};
